import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { relativeTime } from '../features/conversations/format';
import { useUnreadCount } from '../features/direct-messages/hooks/use-direct-messages';
import {
  resolveNav,
  type ResolvedNavItem,
  type ResolvedNavSection,
} from '../features/navigation/nav-model';
import { useCapabilities } from '../hooks/use-capability';
import { useConversations } from '../hooks/use-conversations';
import { avatarColorFor, initialsOf } from '../lib/format';

import type { ConversationListItem } from '../lib/types';

const EXPANDED_KEY = 'lume:sidebar:expanded';

/** Preferências de expand/collapse por seção, persistidas por usuário. */
function loadExpandedPrefs(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    return raw !== null ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

/** Seção que contém a rota atual (para abri-la por padrão). */
function activeSectionId(sections: ResolvedNavSection[], pathname: string): string | null {
  for (const section of sections) {
    for (const item of section.items) {
      if (item.to === undefined) continue;
      if (item.to === '/') {
        if (pathname === '/') return section.id;
      } else if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
        return section.id;
      }
    }
  }
  return null;
}

/**
 * Sidebar canonical — navegação IA de 6 seções (escopo tenant).
 *
 * Spec: docs/superpowers/specs/2026-06-02-navegacao-ia-lume-atendimento-design.md
 *
 * Estrutura (~280px):
 *   1) Navegação por seções rotuladas, derivada de resolveNav(capabilities).
 *      Política D3: seção visível só se habitada; item barrado por tier vira
 *      upsell ('locked'); item planejado vira 'soon' ("em breve").
 *   2) Grupos colapsáveis: ATENDIMENTO (dia-a-dia) sempre aberto; a seção da
 *      rota atual abre por padrão; as demais colapsam para dar espaço vertical
 *      ao preview "Minhas conversas". Escolha do usuário persistida.
 *   3) Preview "Minhas conversas" (top 8 atribuídas) + footer "Ver inbox".
 */
export function Sidebar(): JSX.Element {
  const capabilities = useCapabilities();
  const sections = resolveNav(capabilities);
  // Badge de não-lido da mensageria interna; o hook é cap-gated internamente
  // (retorna 0 sem direct-message-participate) → seguro chamar incondicionalmente.
  const { unread } = useUnreadCount();
  const location = useLocation();
  const [prefs, setPrefs] = useState<Record<string, boolean>>(loadExpandedPrefs);
  const activeId = activeSectionId(sections, location.pathname);
  // D5 (30/Set/2026, prancha 00-B/2a): na Administração o menu é a navegação inteira — 9
  // itens sob a seção. Com o teto de 60% do `.sidebar__nav`, o preview «Minhas conversas»
  // escondia 7 dos 9 domínios atrás de rolagem interna (medido no smoke). A prancha desenha
  // o menu sem o preview nesse contexto; o app segue a prancha SÓ aqui e mantém o rodapé
  // «Ver inbox completa». Fetch gateado, não só o render (Item 555, ponto 4).
  const inAdmin = activeId === 'administracao';
  const { data, isLoading } = useConversations({ assignedToUserId: 'me' }, !inAdmin);
  const myConversations = (data?.data ?? []).slice(0, 8);

  const isOpen = (sectionId: string): boolean => {
    if (sectionId === 'atendimento') return true; // dia-a-dia sempre aberto
    const pref = prefs[sectionId];
    if (pref !== undefined) return pref;
    return sectionId === activeId; // default: abre a seção da rota atual
  };

  const toggle = (sectionId: string): void => {
    const next = { ...prefs, [sectionId]: !isOpen(sectionId) };
    setPrefs(next);
    try {
      localStorage.setItem(EXPANDED_KEY, JSON.stringify(next));
    } catch {
      // persistência best-effort
    }
  };

  return (
    <aside className="sidebar" aria-label="Navegação lateral">
      <nav
        className={inAdmin ? 'sidebar__nav sidebar__nav--full' : 'sidebar__nav'}
        aria-label="Navegação principal"
      >
        {sections.map((section) => {
          const open = isOpen(section.id);
          const collapsible = section.id !== 'atendimento';
          return (
            <div key={section.id} className="sidebar__nav-group">
              {collapsible ? (
                <button
                  type="button"
                  className="sidebar__nav-group-title sidebar__nav-group-toggle"
                  aria-expanded={open}
                  onClick={() => {
                    toggle(section.id);
                  }}
                >
                  <span>{section.title}</span>
                  {open ? (
                    <ChevronDown size={12} aria-hidden />
                  ) : (
                    <ChevronRight size={12} aria-hidden />
                  )}
                </button>
              ) : (
                <h3 className="sidebar__nav-group-title">{section.title}</h3>
              )}
              {open &&
                section.items.map((item) => (
                  <SidebarNavItem
                    key={`${section.id}:${item.label}`}
                    item={item}
                    {...(item.to === '/mensagens' ? { badgeCount: unread } : {})}
                  />
                ))}
            </div>
          );
        })}
      </nav>

      {!inAdmin && (
        <>
          <div className="sidebar__filterbar">
            <h2>Minhas conversas</h2>
          </div>
          <div className="sidebar__subtitle">atribuídas e ativas · {myConversations.length}</div>

          <div className="sidebar__list">
            {isLoading ? (
              <SidebarListSkeleton />
            ) : myConversations.length === 0 ? (
              <p className="sidebar__list-empty">Nenhuma conversa atribuída ativa.</p>
            ) : (
              myConversations.map((c) => (
                <SidebarConvoCard
                  key={c.id}
                  conversation={c}
                  active={location.pathname === `/atendimento/conversation/${c.id}`}
                />
              ))
            )}
          </div>
        </>
      )}

      <div className="sidebar__footer">
        <NavLink to="/inbox" className="sidebar__footer-link">
          <b>Ver inbox completa</b>
          <span aria-hidden>→</span>
        </NavLink>
        <p className="sidebar__footer-meta">Pool · equipe · filtros</p>
      </div>
    </aside>
  );
}

function SidebarNavItem({
  item,
  badgeCount,
}: {
  item: ResolvedNavItem;
  /** Contagem de não-lido (mensageria). Renderiza um pill quando > 0. */
  badgeCount?: number;
}): JSX.Element {
  const Icon = item.icon;

  if (item.state === 'active' && item.to !== undefined) {
    const showBadge = badgeCount !== undefined && badgeCount > 0;
    return (
      <NavLink
        to={item.to}
        end={item.matchChildren !== true}
        className={({ isActive }) =>
          ['sidebar__nav-item', isActive ? 'sidebar__nav-item--active' : '']
            .filter(Boolean)
            .join(' ')
        }
      >
        <Icon className="sidebar__nav-item-icon" size={16} strokeWidth={1.75} aria-hidden />
        <span className="sidebar__nav-item-label">{item.label}</span>
        {showBadge && (
          <span
            className="sidebar__nav-item-badge"
            aria-label={`${badgeCount.toString()} mensagens não lidas`}
          >
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </NavLink>
    );
  }

  if (item.state === 'locked') {
    return (
      <div
        className="sidebar__nav-item sidebar__nav-item--locked"
        aria-disabled
        title={`${item.label} — disponível no ${item.tierLabel ?? 'plano superior'}`}
      >
        <Icon className="sidebar__nav-item-icon" size={16} strokeWidth={1.75} aria-hidden />
        <span className="sidebar__nav-item-label">{item.label}</span>
        {item.tierLabel !== undefined && (
          <span className="sidebar__nav-item-tier-tag">{item.tierLabel}</span>
        )}
      </div>
    );
  }

  // 'soon'
  return (
    <div
      className="sidebar__nav-item sidebar__nav-item--placeholder"
      aria-disabled
      title={`${item.label} — em breve`}
    >
      <Icon className="sidebar__nav-item-icon" size={16} strokeWidth={1.75} aria-hidden />
      <span className="sidebar__nav-item-label">{item.label}</span>
      <span className="sidebar__nav-item-placeholder-tag">em breve</span>
    </div>
  );
}

function SidebarListSkeleton(): JSX.Element {
  return (
    <div aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={`sidebar-skel-${i.toString()}`} className="sidebar__list-skeleton-row">
          <div className="sidebar__list-skeleton-bubble" />
          <div className="sidebar__list-skeleton-lines">
            <div className="sidebar__list-skeleton-line" style={{ width: '70%' }} />
            <div className="sidebar__list-skeleton-line" style={{ width: '45%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SidebarConvoCard({
  conversation,
  active,
}: {
  conversation: ConversationListItem;
  active: boolean;
}): JSX.Element {
  const c = conversation;
  const personName = c.person?.displayName ?? '(sem identidade)';
  const initials = initialsOf(personName);
  const color = avatarColorFor(c.personId ?? c.id);
  return (
    <NavLink
      to={`/atendimento/conversation/${c.id}`}
      className={['convo-card', active ? 'convo-card--selected' : ''].filter(Boolean).join(' ')}
    >
      <span aria-hidden className="user-avatar user-avatar--md" style={{ background: color }}>
        {initials}
      </span>
      <span className="convo-card__body">
        <span className="convo-card__head">
          <span className="convo-card__name">{personName}</span>
          <span className="convo-card__time">{relativeTime(c.updatedAt)}</span>
        </span>
        <span className="convo-card__preview">
          {c.lastMessage?.content ?? 'Sem mensagens ainda.'}
        </span>
      </span>
    </NavLink>
  );
}
